import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useQuasar } from 'quasar'
import { useRoute } from 'vue-router'

import { useConfirm } from 'src/composables/app/system/useConfirm'
import { getSceneKey } from 'src/composables/onboarding/tour-screens'
import { sessionStorageService } from 'src/services/app/storage.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { MOBILE_BLOCK_WIDTH } from 'src/services/consts/app.const'
import { ONBOARDING_STORY, TOUR_STATUS } from 'src/services/consts/onboarding.const'
import { ROUTES } from 'src/router/const'
import { useAccountStore } from 'src/stores/account'

// Walkthrough run state. Only the actions store writes it.
export const useOnboardingTourStore = defineStore('onboarding-tour', () => {
  const $q = useQuasar()
  const route = useRoute()
  const accountStore = useAccountStore()
  const { isOpen: isConfirmOpen } = useConfirm()

  const status = ref(TOUR_STATUS.IDLE)
  const cardIndex = ref(0)
  // The card being prepared. cardIndex keeps the current card up until the next one shows.
  const pendingIndex = ref(0)
  // While idle: a replay is requested. During a run: the run is a replay.
  const isReplay = ref(false)
  // After "Skip for now", session storage keeps this set across reloads until logout.
  const hasAutoStarted = ref(!!sessionStorageService.get(STORAGE_KEYS.ONBOARDING_TOUR_SNOOZED))
  const isSkipConfirmOpen = ref(false)
  // Stops a Back then Next from sending the started event twice.
  const hasLeftWelcome = ref(false)

  const isWideEnough = computed(() => $q.screen.width >= MOBILE_BLOCK_WIDTH)
  const isTourActive = computed(() => status.value !== TOUR_STATUS.IDLE)
  const isSceneChanging = computed(() => status.value === TOUR_STATUS.CHANGING)
  // Read after each await, so a move stops once the run ends.
  const isMoveActive = computed(
    () => status.value === TOUR_STATUS.CHANGING || status.value === TOUR_STATUS.MOVING
  )
  const shownCard = computed(() => {
    const isCardUp = status.value === TOUR_STATUS.SHOWING || status.value === TOUR_STATUS.MOVING
    return isCardUp ? ONBOARDING_STORY[cardIndex.value] : null
  })
  const sceneKey = computed(() => (shownCard.value ? getSceneKey(shownCard.value) : null))
  const canClose = computed(() => isTourActive.value && isReplay.value)
  const canSkip = computed(
    () => status.value === TOUR_STATUS.SHOWING && cardIndex.value === 0 && !isReplay.value
  )

  const canAutoStart = computed(
    () =>
      status.value === TOUR_STATUS.IDLE &&
      route.name === ROUTES.HOME &&
      !route.query.ticketId &&
      !route.query.approvalId &&
      !!accountStore.accountData &&
      !accountStore.error &&
      isWideEnough.value &&
      !isConfirmOpen.value &&
      (isReplay.value || (!hasAutoStarted.value && accountStore.showOnboardingTour))
  )

  const canContinueTour = computed(() => {
    if (status.value !== TOUR_STATUS.SHOWING) return true

    const { route: sceneRoute, params = {}, query = {} } = ONBOARDING_STORY[cardIndex.value].scene
    const isOnCardScreen =
      route.name === sceneRoute &&
      route.params.ticketStep === params.ticketStep &&
      route.params.ticketId === params.ticketId &&
      route.query.ticketId === query.ticketId &&
      route.query.approvalId === query.approvalId

    return isWideEnough.value && !isConfirmOpen.value && !accountStore.error && isOnCardScreen
  })

  return {
    status,
    cardIndex,
    pendingIndex,
    isReplay,
    hasAutoStarted,
    isSkipConfirmOpen,
    hasLeftWelcome,

    cardCount: ONBOARDING_STORY.length,
    isWideEnough,
    isTourActive,
    isSceneChanging,
    isMoveActive,
    shownCard,
    sceneKey,
    canClose,
    canSkip,
    canAutoStart,
    canContinueTour,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOnboardingTourStore, import.meta.hot))
}
